import { supabase } from "@/lib/supabase";
import { toast } from "@heroui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ManageRecipesResult } from "./useGetRecipesForManage";
import type { Recipe } from "@/features/Recipes-renamed/types";
import { invalidateRecipeQueries } from "@/lib/recipeCacheKeys";

interface ToggleFavoriteVars {
    id: number;
    favorite: boolean;
}

export function useToggleFavorite() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, favorite }: ToggleFavoriteVars) => {
            const { error } = await supabase.from("recipes").update({ favorite }).eq("id", id);
            if (error) throw error;
        },
        onMutate: async ({ id, favorite }) => {
            await queryClient.cancelQueries({ queryKey: ["manage-recipes"] });
            await queryClient.cancelQueries({ queryKey: ["recipe", id] });

            // Snapshot every active manage-recipes cache entry (different params keys), then
            // patch them all so the optimistic flip is visible regardless of which page/filter is active.
            const snapshots = queryClient.getQueriesData<ManageRecipesResult>({ queryKey: ["manage-recipes"] });
            queryClient.setQueriesData<ManageRecipesResult>({ queryKey: ["manage-recipes"] }, (old) => {
                if (!old) return old;
                return {
                    ...old,
                    rows: old.rows.map((row) => (row.id === id ? { ...row, favorite } : row))
                };
            });

            // The detail view's favorite control reads recipe.favorite, so patch
            // it too — the toggle flips immediately instead of waiting for refetch.
            const recipeSnapshot = queryClient.getQueryData<Recipe>(["recipe", id]);
            queryClient.setQueryData<Recipe>(["recipe", id], (old) => (old ? { ...old, favorite } : old));

            return { snapshots, recipeSnapshot };
        },
        onError: (_err, vars, context) => {
            context?.snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
            if (context?.recipeSnapshot) {
                queryClient.setQueryData(["recipe", vars.id], context.recipeSnapshot);
            }
            toast.danger("Couldn't update favorite", { timeout: 3000 });
        },
        onSettled: (_data, _err, vars) => {
            invalidateRecipeQueries(queryClient, vars.id);
        }
    });
}
